import { Component, inject, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialog,
} from "@angular/material/dialog";
import { AgGridAngular } from "ag-grid-angular";
import type { ColDef } from "ag-grid-community";
import { Store } from "@ngrx/store";
import { Subject, takeUntil } from "rxjs";
import { ExistingPacket } from "@models/mips-stepper.model";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import { ConfrimationDialogData } from "@models/confirmation-dialog-data.model";
import { standardTheme } from "@styles/ag-grid-theme";
import { SubmissionActionsRendererComponent } from "@components/grid-components/submission-actions-renderer/submission-actions-renderer.component";

export interface SubmissionsDialogData {
  submissions: ExistingPacket[];
}

@Component({
  selector: "app-mips-submissions-dialog",
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    AgGridAngular,
  ],
  templateUrl: "./mips-submissions-dialog.component.html",
  styleUrls: ["./mips-submissions-dialog.component.css"],
})
export class MipsSubmissionsDialogComponent implements OnDestroy {
  private readonly dialogRef = inject(
    MatDialogRef<MipsSubmissionsDialogComponent>,
  );
  readonly data = inject<SubmissionsDialogData>(MAT_DIALOG_DATA);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();

  rowData: ExistingPacket[] = [...this.data.submissions];
  quickFilterText = "";
  readonly standardTheme = standardTheme;

  readonly defaultColDef: ColDef = {
    flex: 1,
    sortable: true,
    filter: true,
    resizable: true,
  };

  readonly colDefs: ColDef[] = [
    {
      field: "performanceYear",
      headerName: "Perf. Year",
      flex: 0,
      width: 110,
      tooltipValueGetter: (p) => p.value ?? "",
    },
    {
      field: "npi",
      headerName: "NPI",
      flex: 0,
      width: 120,
      tooltipValueGetter: (p) => p.value ?? "",
    },
    {
      field: "clinicianName",
      headerName: "Clinician",
      valueFormatter: (p) => p.value ?? "—",
      tooltipValueGetter: (p) => p.value ?? "",
    },
    {
      field: "reportingType",
      headerName: "Program",
      valueFormatter: (p) => this.formatReportingType(p.value),
      tooltipValueGetter: (p) => this.formatReportingType(p.value),
    },
    {
      field: "reportingLevel",
      headerName: "Submission Type",
      valueFormatter: (p) => this.formatReportingLevel(p.value),
      tooltipValueGetter: (p) => this.formatReportingLevel(p.value),
    },
    {
      field: "status",
      headerName: "Status",
      valueFormatter: (p) =>
        p.value ? p.value.charAt(0).toUpperCase() + p.value.slice(1) : "—",
      tooltipValueGetter: (p) =>
        p.value ? p.value.charAt(0).toUpperCase() + p.value.slice(1) : "",
      cellStyle: (p) => {
        if (p.value === "draft")
          return { color: "#e65100", backgroundColor: "#fff3e0" };
        if (p.value === "submitted")
          return { color: "#2e7d32", backgroundColor: "#e8f5e9" };
        return null;
      },
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      valueFormatter: (p) =>
        p.value
          ? new Date(p.value).toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })
          : "—",
      tooltipValueGetter: (p) =>
        p.value
          ? new Date(p.value).toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })
          : "",
    },
    {
      headerName: "",
      cellRenderer: SubmissionActionsRendererComponent,
      cellRendererParams: {
        onContinue: (packet: ExistingPacket) => this.continueSubmission(packet),
        onDelete: (packet: ExistingPacket) => this.confirmDelete(packet),
        isContinueBlocked: (packet: ExistingPacket) =>
          packet.reportingLevel === "group" &&
          packet.status !== "submitted" &&
          this.rowData.some(
            (p) =>
              p.reportingLevel === "group" &&
              p.reportingType === packet.reportingType &&
              p.status === "submitted",
          ),
        isEditable: (packet: ExistingPacket) => {
          const cutoff = new Date(packet.performanceYear + 1, 3, 1); // April 1 of next year
          return new Date() < cutoff;
        },
      },
      flex: 0,
      width: 165,
      sortable: false,
      filter: false,
      resizable: false,
    },
  ];

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  continueSubmission(packet: ExistingPacket): void {
    this.dialogRef.close({ action: "continue", packet });
  }

  confirmDelete(packet: ExistingPacket): void {
    const confirmRef = this.dialog.open<
      ConfirmationModalComponent,
      ConfrimationDialogData,
      boolean
    >(ConfirmationModalComponent, {
      data: {
        title: "Delete Submission",
        content: `Are you sure you want to delete this ${this.formatReportingType(packet.reportingType)} ${this.formatReportingLevel(packet.reportingLevel)} submission for performance year ${packet.performanceYear}? This action cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel",
      },
    });

    confirmRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            MipsPacketActions.deleteSubmission({
              packetId: packet.id,
              organizationId: parseInt(packet.practice, 10),
            }),
          );
          this.rowData = this.rowData.filter((p) => p.id !== packet.id);
        }
      });
  }

  formatReportingType(value: string): string {
    const map: Record<string, string> = {
      traditional: "Traditional MIPS",
      mvp: "MVP",
      both: "Both",
    };
    return map[value] ?? value;
  }

  formatReportingLevel(value: string): string {
    const map: Record<string, string> = {
      individual: "Individual",
      group: "Group",
      subgroup: "Subgroup",
    };
    return map[value] ?? value;
  }
}
